import type { Favorite } from '$lib/components/profile/favorite/types';
import type {
	AddToFavoritesEvent,
	DeleteFavoritesEvent,
	DisplayFavoritesEvent,
	ShowFavoritesContext,
	ShowFavoritesEvent,
} from '$lib/services/tracking/googleAnalytics/events';
import { track } from '$lib/services/tracking/googleAnalytics';

type EventName = AddToFavoritesEvent['action'] | DeleteFavoritesEvent['action'] | DisplayFavoritesEvent['action'] | ShowFavoritesEvent['action'];

const events = {
	ADD_TO_FAVORITES: 'add_iti_to_favorites',
	DELETE_FAVORITES: 'delete_iti_favorites',
	DISPLAY_FAVORITES: 'display_iti_favorites',
	SHOW_FAVORITES: 'see_your_favorites',
} as const satisfies Record<string, EventName>;

function buildPartialItineraryPayloadFromFavorites(favorite: Favorite) {
	return {
		city_departure: favorite.departureName,
		city_arrival: favorite.arrivalName,
		latitude_departure: favorite.coordinates[0].lat.toString(),
		longitude_departure: favorite.coordinates[0].lng.toString(),
		latitude_arrival: favorite.coordinates[favorite.coordinates.length - 1].lat.toString(),
		longitude_arrival: favorite.coordinates[favorite.coordinates.length - 1].lng.toString(),
		distance: favorite.distance.value.toString(),
		cost: favorite.totalCost.amount.toString(),
		user_currency: favorite.currency,
		travel_mode: favorite.mode,
		drive_with_electric_vehicle: favorite.isEv.toString(),
		drive_with_caravan: favorite.withCaravan.toString(),
		route_preference: favorite.constraints,
		distance_system: favorite.distanceSystem,
		duration: favorite.duration.toString(),
		total_cost: favorite.totalCost.amount.toString(),
		timestamp: favorite.timestamp.toString(),
		car_id: favorite.carId || undefined,
	};
}

export function trackAddToFavorites(storedFavoritesCount: number) {
	track({
		action: events.ADD_TO_FAVORITES,
		payload: {
			action: 'click',
			nb_iti_favorites: storedFavoritesCount.toString(),
		},
	} satisfies AddToFavoritesEvent);
}

export function trackDeleteFavorites(storedFavoritesCount: number, favorite: Favorite) {
	track({
		action: events.DELETE_FAVORITES,
		payload: {
			action: 'delete',
			nb_iti_favorites: storedFavoritesCount.toString(),
			...buildPartialItineraryPayloadFromFavorites(favorite),
		},
	} satisfies DeleteFavoritesEvent);
}

export function trackDisplayFavorites(storedFavoritesCount: number, favorite: Favorite) {
	track({
		action: events.DISPLAY_FAVORITES,
		payload: {
			action: 'selection',
			nb_iti_favorites: storedFavoritesCount.toString(),
			...buildPartialItineraryPayloadFromFavorites(favorite),
		},
	} satisfies DisplayFavoritesEvent);
}

export function trackShowFavorites(context: ShowFavoritesContext, storedFavoritesCount: number) {
	track({
		action: events.SHOW_FAVORITES,
		payload: {
			action: 'click',
			context,
			nb_iti_favorites: storedFavoritesCount.toString(),
		},
	} satisfies ShowFavoritesEvent);
}
